// src/components/AddCompanyForm/PermissionsCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardHeader, CardContent, Grid, Stack, Alert, Avatar, Chip, TextField, CircularProgress, Button, Box, Typography
} from "@mui/material";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { Autocomplete } from "@mui/material";
import { fetchData } from "../../api/client";
import { useTranslation } from "react-i18next";
import { extractCompanyAclUsers, mergeAutocompleteUsers } from "../../utils/companyAcl";

export default function PermissionsCard({ companyId, draftUsers, onDraftUsersChange }) {
  const { t } = useTranslation("settings");
  const isDraft = !companyId;

  const [aclLoading, setAclLoading] = useState(false);
  const [aclSaving, setAclSaving] = useState(false);
  const [aclError, setAclError] = useState(null);
  const [snack, setSnack] = useState(null);

  const [aclSelectedUsers, setAclSelectedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [usersQuery, setUsersQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);

  const displayUsers = isDraft ? (draftUsers || []) : aclSelectedUsers;
  const setUsers = (next) => {
    if (isDraft) onDraftUsersChange?.(next);
    else setAclSelectedUsers(next);
  };

  // load current ACL (edição)
  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    setAclLoading(true);
    setAclError(null);
    fetchData(`/companies/${companyId}/acl`, "GET")
      .then((resp) => {
        if (!alive) return;
        setAclSelectedUsers(extractCompanyAclUsers(resp));
      })
      .catch((e) =>
        alive &&
        setAclError(
          (typeof e?.message === "string" && e.message) || t("addCompanyForm.permissions.loadError")
        )
      )
      .finally(() => alive && setAclLoading(false));
    return () => { alive = false; };
  }, [companyId, t]);

  // search users
  useEffect(() => {
    let alive = true;
    const term = String(usersQuery || "").trim();
    const timer = setTimeout(() => {
      setUsersLoading(true);
      fetchData(`/users?search=${encodeURIComponent(term)}&limit=20`, "GET")
        .then((resp) => {
          if (!alive) return;
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          setUserOptions(rows.map((u) => ({ id: u.id, nome: u.nome || "", email: u.email || "" })));
        })
        .catch(() => alive && setUserOptions([]))
        .finally(() => alive && setUsersLoading(false));
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [usersQuery]);

  const mergedOptions = useMemo(
    () => mergeAutocompleteUsers(userOptions, displayUsers),
    [userOptions, displayUsers]
  );

  const handleSave = async () => {
    if (!companyId) {
      setSnack({ severity: "error", message: t("addCompanyForm.permissions.saveBeforeDraft") });
      return;
    }
    try {
      setAclSaving(true);
      setAclError(null);
      const users = aclSelectedUsers.map((u) => Number(u.id)).filter(Boolean);
      await fetchData(`/companies/${companyId}/acl`, "PUT", { users });
      setSnack({ severity: "success", message: t("addCompanyForm.permissions.saveSuccess") });
    } catch (e) {
      const msg =
        (typeof e?.message === "string" && e.message) ||
        e?.response?.data?.message ||
        t("addCompanyForm.permissions.saveError");
      setAclError(msg);
      setSnack({ severity: "error", message: t("addCompanyForm.permissions.saveFailed") });
    } finally {
      setAclSaving(false);
      setTimeout(() => setSnack(null), 2500);
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <SecurityRoundedIcon fontSize="small" />
            <span>{t("addCompanyForm.permissions.title")}</span>
          </Stack>
        }
        subheader={
          isDraft
            ? t("addCompanyForm.permissions.subheaderDraft")
            : t("addCompanyForm.permissions.subheaderEdit")
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {aclError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{aclError}</Alert>
          </Box>
        )}
        {snack && (
          <Box sx={{ mb: 2 }}>
            <Alert severity={snack.severity}>{snack.message}</Alert>
          </Box>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              options={mergedOptions}
              value={displayUsers}
              loading={usersLoading || aclLoading}
              onChange={(_, val) => setUsers(val)}
              onInputChange={(_, val) => setUsersQuery(val)}
              getOptionLabel={(opt) =>
                opt?.nome ? `${opt.nome}${opt.email ? ` — ${opt.email}` : ""}` : opt?.email || ""
              }
              isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 24, height: 24 }}>
                      {(option.nome || option.email || "?").slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {option.nome || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email || ""}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.nome || option.email || `ID ${option.id}`}
                    variant="outlined"
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("addCompanyForm.permissions.usersLabel")}
                  placeholder={t("addCompanyForm.permissions.searchPlaceholder")}
                  helperText={
                    isDraft
                      ? t("addCompanyForm.permissions.helperDraft")
                      : t("addCompanyForm.permissions.helperEdit")
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading || aclLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            {!isDraft ? (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={handleSave} disabled={aclSaving}>
                  {aclSaving ? t("addCompanyForm.permissions.saving") : t("addCompanyForm.permissions.save")}
                </Button>
              </Stack>
            ) : (
              <Box />
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
